// src/pages/AdminProgressView.jsx
// Admin/trainer view: set category targets per student (or bulk),
// then see every student's progress against those targets.

import { useState, useEffect } from "react";
import {
  CATEGORIES,
  setBulkTargets, getAllProgressReport, subscribeProgressLogs,
  subscribeCategories, subscribeAllRecentLogs
} from "../firebase/database";
import { getAllStudents } from "../firebase/database";
import { minutesToHHMM, HHMMToMinutes } from "../utils/helpers";
import { theme } from "../utils/theme";

const C = theme.colors;
const CAT_COLORS = {
  theory: theme.colors.accent,
  practical: theme.colors.green,
  assignment: theme.colors.purple,
  revision: theme.colors.amber,
};

function Badge({ children, color=C.accent }) {
  return <span style={{fontSize:11,fontWeight:600,padding:"2px 8px",borderRadius:20,background:color+"1A",color,letterSpacing:.3}}>{children}</span>;
}

function MiniBar({ pct, color }) {
  return (
    <div style={{height:6,borderRadius:3,background:C.dim,width:"100%"}}>
      <div style={{width:Math.min(100,pct)+"%",height:"100%",borderRadius:3,background:color,transition:"width .4s"}}/>
    </div>
  );
}

export default function AdminProgressView() {
  const [tab, setTab]           = useState("overview");
  const [students, setStudents] = useState([]);
  const [report, setReport]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [categories, setCategories] = useState(CATEGORIES);
  const [targets, setTargets]   = useState({ theory:"40:00", practical:"60:00", assignment:"20:00", revision:"30:00" });
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentLogs, setStudentLogs] = useState([]);

  useEffect(() => {
    Promise.all([getAllStudents(), getAllProgressReport()]).then(([studs, rep]) => {
      setStudents(studs.filter(s => s.status !== "inactive"));
      setReport(rep);
      setLoading(false);
    });

    const unsubCats = subscribeCategories(setCategories);
    return () => unsubCats();
  }, []);

  useEffect(() => {
    if (!selectedStudent) return;
    const unsub = subscribeProgressLogs(selectedStudent.id, setStudentLogs);
    return unsub;
  }, [selectedStudent]);

  const handleSetBulkTargets = async () => {
    setSaving(true);
    const ids = students.map(s => s.id);
    await setBulkTargets(ids, targets);
    const rep = await getAllProgressReport();
    setReport(rep);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const totalStudents    = report.length;
  const onTrack          = report.filter(s => s.overallRate >= 75).length;
  const atRisk           = report.filter(s => s.overallRate > 0 && s.overallRate < 75).length;
  const notStarted       = report.filter(s => s.overallRate === 0).length;
  const avgRate          = report.length ? Math.round(report.reduce((a,s)=>a+s.overallRate,0)/report.length) : 0;

  if (loading) return <div style={{padding:40,textAlign:"center",color:C.muted,fontFamily:"DM Sans"}}>Loading progress data…</div>;

  return (
    <div style={{fontFamily:"'DM Sans',sans-serif"}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400&display=swap'); *{box-sizing:border-box;} @keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}} .fade{animation:fadeIn .3s ease}`}</style>

      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:22}}>
        <div>
          <div style={{fontSize:20,fontWeight:700,color:C.text}}>Student Progress Checkup</div>
          <div style={{fontSize:13,color:C.muted}}>Track hours logged vs targets across all categories</div>
        </div>
      </div>

      {/* Summary cards */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:22}}>
        {[
          ["Avg Progress", `${avgRate}%`, avgRate>=75?C.green:avgRate>=50?C.amber:C.red],
          ["On Track", onTrack, C.green],
          ["At Risk", atRisk, C.red],
          ["Not Started", notStarted, C.muted],
        ].map(([l,v,c])=>(
          <div key={l} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:"16px 20px"}}>
            <div style={{fontSize:12,color:C.muted,marginBottom:6,fontWeight:500}}>{l}</div>
            <div style={{fontSize:28,fontWeight:700,color:c,lineHeight:1}}>{v}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{display:"flex",gap:4,marginBottom:20,borderBottom:`1px solid ${C.border}`}}>
        {[["overview","Overview"],["table","Student Table"],["targets","Set Targets"],["logs","Recent Logs"]].map(([id,label])=>(
          <button key={id} onClick={()=>setTab(id)} style={{padding:"8px 16px",fontSize:14,fontWeight:tab===id?600:400,color:tab===id?C.accent:C.muted,background:"none",border:"none",borderBottom:tab===id?`2px solid ${C.accent}`:"2px solid transparent",cursor:"pointer",fontFamily:"DM Sans",marginBottom:-1}}>
            {label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {tab==="overview" && (
        <div className="fade">
          {/* Category breakdown across all students */}
          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:20,marginBottom:16}}>
            <div style={{fontSize:14,fontWeight:600,color:C.text,marginBottom:16}}>Category-wise Progress (All Students)</div>
            {categories.map(cat=>{
              const color = cat.color || CAT_COLORS[cat.id] || C.accent;
              const totalLogged = report.reduce((a,s)=>{
                const cd = s.categoryData.find(c=>c.id===cat.id);
                return a+(cd?.totalLoggedMinutes||0);
              },0);
              const totalTarget = report.reduce((a,s)=>{
                const cd = s.categoryData.find(c=>c.id===cat.id);
                return a+(cd?.adminTargetMinutes||0);
              },0);
              const pct = totalTarget>0?Math.min(100,Math.round((totalLogged/totalTarget)*100)):0;
              return (
                <div key={cat.id} style={{marginBottom:16}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                    <span style={{fontSize:13,fontWeight:500,color}}>{cat.label}</span>
                    <div style={{display:"flex",gap:16,fontSize:12,color:C.muted}}>
                      <span>{minutesToHHMM(totalLogged)} logged</span>
                      <span>Target: {minutesToHHMM(totalTarget)}</span>
                      <span style={{fontWeight:600,color:pct>=75?C.green:pct>=50?C.amber:C.red}}>{pct}%</span>
                    </div>
                  </div>
                  <MiniBar pct={pct} color={color}/>
                </div>
              );
            })}
          </div>

          {/* At-risk students */}
          {atRisk>0&&(
            <div style={{background:"#FEF2F2",border:"1px solid #FECACA",borderRadius:12,padding:20}}>
              <div style={{fontSize:14,fontWeight:600,color:C.red,marginBottom:12}}>Students Needing Attention</div>
              {report.filter(s=>s.overallRate>0&&s.overallRate<75).map(s=>(
                <div key={s.id} style={{display:"flex",alignItems:"center",gap:12,marginBottom:12,cursor:"pointer"}} onClick={()=>{setSelectedStudent(s);setTab("logs");}}>
                  <div style={{width:32,height:32,borderRadius:"50%",background:"#FEE2E2",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:C.red}}>
                    {s.name.split(" ").map(w=>w[0]).join("").slice(0,2)}
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:500,color:C.text}}>{s.name}</div>
                    <div style={{fontSize:11,color:C.muted}}>Roll #{s.roll} · {s.batch} · {s.totalEntries} entries</div>
                  </div>
                  <div style={{width:100}}>
                    <MiniBar pct={s.overallRate} color={C.red}/>
                    <div style={{fontSize:11,color:C.red,fontWeight:600,textAlign:"right",marginTop:3}}>{s.overallRate}%</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── TABLE ── */}
      {tab==="table" && (
        <div className="fade" style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,overflow:"hidden"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead>
              <tr style={{background:C.bg}}>
                <th style={{padding:"10px 14px",textAlign:"left",fontSize:12,color:C.muted,fontWeight:600}}>Student</th>
                <th style={{padding:"10px 14px",textAlign:"left",fontSize:12,color:C.muted,fontWeight:600}}>Roll</th>
                {categories.map(cat=>(
                  <th key={cat.id} style={{padding:"10px 14px",textAlign:"center",fontSize:12,color:cat.color||CAT_COLORS[cat.id],fontWeight:600}}>{cat.label.split(" ")[0]}</th>
                ))}
                <th style={{padding:"10px 14px",textAlign:"left",fontSize:12,color:C.muted,fontWeight:600}}>Overall</th>
                <th style={{padding:"10px 14px",textAlign:"left",fontSize:12,color:C.muted,fontWeight:600}}>Entries</th>
              </tr>
            </thead>
            <tbody>
              {report.sort((a,b)=>b.overallRate-a.overallRate).map((s,i)=>(
                <tr key={s.id} style={{borderTop:i>0?`1px solid ${C.border}`:"none",cursor:"pointer"}} onClick={()=>{setSelectedStudent(s);setTab("logs");}}>
                  <td style={{padding:"10px 14px",fontSize:14,color:C.text,fontWeight:500}}>{s.name}</td>
                  <td style={{padding:"10px 14px",fontSize:12,color:C.muted,fontFamily:"monospace"}}>{s.roll}</td>
                  {s.categoryData.map(cat=>(
                    <td key={cat.id} style={{padding:"10px 14px",textAlign:"center"}}>
                      <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                        <span style={{fontSize:12,fontWeight:600,color:cat.rate>=75?C.green:cat.rate>=50?C.amber:cat.rate>0?C.red:C.muted}}>
                          {cat.adminTargetMinutes>0?`${cat.rate}%`:"—"}
                        </span>
                        {cat.adminTargetMinutes>0&&(
                          <div style={{width:48}}>
                            <MiniBar pct={cat.rate} color={cat.color || CAT_COLORS[cat.id] || C.accent}/>
                          </div>
                        )}
                        <span style={{fontSize:10,color:C.muted,fontFamily:"monospace"}}>{minutesToHHMM(cat.totalLoggedMinutes)}</span>
                      </div>
                    </td>
                  ))}
                  <td style={{padding:"10px 14px"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <div style={{width:56,height:5,borderRadius:3,background:C.dim}}>
                        <div style={{width:s.overallRate+"%",height:"100%",borderRadius:3,background:s.overallRate>=75?C.green:s.overallRate>=50?C.amber:s.overallRate>0?C.red:C.dim}}/>
                      </div>
                      <span style={{fontSize:13,fontWeight:600,color:s.overallRate>=75?C.green:s.overallRate>=50?C.amber:s.overallRate>0?C.red:C.muted}}>{s.overallRate}%</span>
                    </div>
                  </td>
                  <td style={{padding:"10px 14px",fontSize:13,color:C.muted}}>{s.totalEntries}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── SET TARGETS ── */}
      {tab==="targets" && (
        <div className="fade">
          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:24,maxWidth:500}}>
            <div style={{fontSize:15,fontWeight:600,color:C.text,marginBottom:6}}>Set Targets for All Students</div>
            <div style={{fontSize:13,color:C.muted,marginBottom:20}}>These targets apply to every enrolled student. Enter time as HH:MM (e.g. 40:00 = 40 hours).</div>
            {categories.map(cat=>{
              const color = cat.color || CAT_COLORS[cat.id] || C.accent;
              return (
                <div key={cat.id} style={{marginBottom:16}}>
                  <label style={{fontSize:13,color:color,display:"flex",alignItems:"center",gap:8,marginBottom:8,fontWeight:500}}>
                    <span style={{width:10,height:10,borderRadius:"50%",background:color,display:"inline-block",flexShrink:0}}/>
                    {cat.label}
                  </label>
                  <input
                    value={targets[cat.id] || "00:00"}
                    onChange={e=>setTargets(t=>({...t,[cat.id]:e.target.value}))}
                    placeholder="40:00"
                    style={{width:"100%",background:"#F9FAFB",border:`1px solid ${C.border}`,borderRadius:9,padding:"10px 13px",fontSize:15,color:C.text,outline:"none",fontFamily:"DM Mono, monospace",letterSpacing:2}}
                  />
                </div>
              );
            })}
            <button onClick={handleSetBulkTargets} disabled={saving} style={{width:"100%",padding:13,borderRadius:10,background:saved?C.green:C.accent,color:"#fff",border:"none",fontSize:15,fontWeight:600,cursor:"pointer",fontFamily:"DM Sans",marginTop:4}}>
              {saving?"Saving…":saved?`Targets set for ${students.length} students ✓`:`Apply to all ${students.length} students`}
            </button>
            <div style={{fontSize:12,color:C.muted,marginTop:10,textAlign:"center"}}>You can also set individual targets per student from the student profile.</div>
          </div>
        </div>
      )}

      {/* ── RECENT LOGS ── */}
      {tab==="logs" && (
        <div className="fade">
          {/* Student filter pills */}
          <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:16}}>
            <button onClick={()=>setSelectedStudent(null)} style={{fontSize:12,padding:"5px 12px",borderRadius:20,border:`1px solid ${!selectedStudent?C.accent:C.border}`,background:!selectedStudent?C.accentSoft:"transparent",color:!selectedStudent?C.accent:C.muted,cursor:"pointer",fontFamily:"DM Sans"}}>All</button>
            {students.map(s=>(
              <button key={s.id} onClick={()=>setSelectedStudent(s)} style={{fontSize:12,padding:"5px 12px",borderRadius:20,border:`1px solid ${selectedStudent?.id===s.id?C.accent:C.border}`,background:selectedStudent?.id===s.id?C.accentSoft:"transparent",color:selectedStudent?.id===s.id?C.accent:C.muted,cursor:"pointer",fontFamily:"DM Sans"}}>
                {s.name.split(" ")[0]}
              </button>
            ))}
          </div>

          <LogsPanel studentId={selectedStudent?.id} allStudents={students} report={report} categories={categories} />
        </div>
      )}
    </div>
  );
}

// ── Sub-component: logs list ──────────────────────────────────────────────────
function LogsPanel({ studentId, allStudents, report, categories }) {
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    if (!studentId) {
      // Gather last 30 logs across all students from report
      const all = report.flatMap(s => {
        const stu = allStudents.find(x=>x.id===s.id);
        return (s._logs||[]).map(l=>({...l,studentName:stu?.name||s.name,studentRoll:stu?.roll||s.roll}));
      }).sort((a,b)=>b.savedAt?.localeCompare(a.savedAt)).slice(0,30);
      setLogs(all);
      return;
    }
    const unsub = subscribeProgressLogs(studentId, setLogs);
    return unsub;
  }, [studentId, report]);

  if (!studentId) {
    // Show recent from all students via report
    const allLogs = report.flatMap(s =>
      s.categoryData.length > 0 ? [{
        _summary: true,
        name: s.name, roll: s.roll, overallRate: s.overallRate,
        totalLogged: s.totalLogged, totalEntries: s.totalEntries,
        categoryData: s.categoryData,
      }] : []
    ).slice(0,20);

    return (
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        {allLogs.map((s,i)=>(
          <div key={i} style={{background:"#FFFFFF",border:"1px solid #E4E7F0",borderRadius:12,padding:18}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
              <div>
                <span style={{fontSize:14,fontWeight:600,color:"#111827"}}>{s.name}</span>
                <span style={{fontSize:12,color:"#6B7280",marginLeft:8}}>Roll #{s.roll} · {s.totalEntries} entries</span>
              </div>
              <span style={{fontSize:14,fontWeight:700,color:s.overallRate>=75?"#16A349":s.overallRate>=50?"#D97706":"#DC2626"}}>{s.overallRate}%</span>
            </div>
            <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
              {s.categoryData.filter(c=>c.adminTargetMinutes>0).map(cat=>{
                const color = cat.color || CAT_COLORS[cat.id] || "#3D7EFF";
                return (
                  <div key={cat.id} style={{flex:"1 1 180px"}}>
                    <div style={{fontSize:11,color:color,fontWeight:600,marginBottom:4}}>{cat.label}</div>
                    <div style={{height:5,borderRadius:3,background:"#E5E7EB"}}>
                      <div style={{width:Math.min(100,cat.rate)+"%",height:"100%",borderRadius:3,background:color}}/>
                    </div>
                    <div style={{fontSize:11,color:"#6B7280",marginTop:3}}>{minutesToHHMM(cat.totalLoggedMinutes)} / {minutesToHHMM(cat.adminTargetMinutes)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      {logs.length===0&&<div style={{textAlign:"center",padding:40,color:"#6B7280",fontSize:14}}>No entries yet.</div>}
      {logs.map(entry=>{
        const catObj = categories.find(c=>c.id===entry.category);
        const color = catObj?.color || CAT_COLORS[entry.category] || "#3D7EFF";
        const pct   = entry.targetMinutes>0?Math.min(100,Math.round((entry.loggedMinutes/entry.targetMinutes)*100)):null;
        return (
          <div key={entry.entryId} style={{background:"#FFFFFF",border:"1px solid #E4E7F0",borderRadius:12,padding:16,borderLeft:`3px solid ${color}`}}>
            <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:8}}>
              <div>
                <span style={{fontSize:12,fontWeight:600,color,marginRight:8}}>{catObj?.label || entry.category}</span>
                <span style={{fontSize:14,fontWeight:500,color:"#111827"}}>{entry.topicName}</span>
              </div>
              <div style={{display:"flex",gap:12,alignItems:"center"}}>
                <span style={{fontSize:11,color:"#6B7280",fontFamily:"monospace"}}>{entry.date}</span>
              </div>
            </div>
            <div style={{display:"flex",gap:16,fontSize:12,color:"#6B7280",marginBottom:entry.notes?8:0,flexWrap:"wrap"}}>
              <span>Time: <strong style={{color:"#111827",fontFamily:"monospace"}}>{entry.startTime} - {entry.endTime}</strong></span>
              <span>Logged: <strong style={{color:"#111827",fontFamily:"monospace"}}>{minutesToHHMM(entry.loggedMinutes)}</strong></span>
              {entry.targetMinutes>0&&<span>Target: <strong style={{color:"#111827",fontFamily:"monospace"}}>{minutesToHHMM(entry.targetMinutes)}</strong></span>}
              {pct!==null&&<span style={{fontWeight:600,color:pct>=75?"#16A349":pct>=50?"#D97706":"#DC2626"}}>{pct}%</span>}
            </div>
            {entry.notes&&<div style={{fontSize:12,color:"#6B7280",lineHeight:1.6,background:"#F9FAFB",padding:"8px 12px",borderRadius:7}}>{entry.notes}</div>}
          </div>
        );
      })}
    </div>
  );
}
